import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {MutationObserver | null} */
  #swatchDropdownObserver = null;
  /** @type {Map<string, object|null>} */
  #subscriptionDataCache = new Map();
  /** @type {object|null} */
  #currentSubscriptionData = null;

  get productPageUrl() {
    // Direct override: when quick-add is triggered outside a product grid (e.g. from the
    // cart drawer recommendations) there is no <product-card> ancestor to read the link
    // from, so the trigger supplies the product URL via data-product-url.
    const directUrl = this.dataset.productUrl;
    if (directUrl) {
      const url = new URL(directUrl, window.location.origin);
      if (!url.searchParams.has('variant')) {
        const selectedVariantId = this.#getSelectedVariantId();
        if (selectedVariantId) url.searchParams.set('variant', selectedVariantId);
      }
      return url.toString();
    }

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      // Cart-launched quick-add (data-product-url set): show variant options as clean
      // dropdowns even when the product uses image-swatch / button pickers on its PDP.
      if (this.dataset.productUrl) this.#startSwatchDropdownConversion();
    }

    this.#openQuickAddModal();
  };

  /**
   * Cart UX: render swatch/button variant options as a clean <select> inside the modal.
   * The original radios stay in the DOM (hidden), so the existing variant-picker change
   * logic still resolves the variant — the dropdown just proxies clicks to them. The PDP
   * is untouched. A MutationObserver re-applies it if the picker re-renders on selection.
   */
  #convertModalSwatchesToDropdowns = () => {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    // Native swatch/button pickers AND the theme's custom image-thumbnail picker.
    const selector = '.variant-option--swatches, .variant-option--buttons, .variant-thumbnail-option';
    for (const fieldset of modalContent.querySelectorAll(selector)) {
      if (fieldset.querySelector('.quick-add-variant-select')) continue;
      const radios = /** @type {HTMLInputElement[]} */ ([...fieldset.querySelectorAll('input[type="radio"]')]);
      if (radios.length === 0) continue;

      // Option name from the legend, with any selected-value echo stripped.
      const legend = fieldset.querySelector('legend');
      let labelText = '';
      if (legend) {
        const clone = /** @type {HTMLElement} */ (legend.cloneNode(true));
        clone.querySelectorAll('.variant-option__swatch-value').forEach((node) => node.remove());
        labelText = clone.textContent.trim();
      }

      const select = document.createElement('select');
      select.className = 'variant-option__select quick-add-variant-select';
      if (labelText) select.setAttribute('aria-label', labelText);
      for (const radio of radios) {
        const option = document.createElement('option');
        option.value = radio.value;
        option.textContent = radio.value;
        if (radio.getAttribute('data-option-available') === 'false') {
          option.disabled = true;
          option.textContent += ' — Sold out';
        }
        if (radio.checked) option.selected = true;
        select.appendChild(option);
      }
      // Proxy to the original radio so the variant-picker's own resolution logic runs.
      select.addEventListener('change', () => {
        radios.find((radio) => radio.value === select.value)?.click();
      });

      const label = document.createElement('span');
      label.className = 'quick-add-variant-label';
      label.setAttribute('aria-hidden', 'true');
      label.textContent = labelText;

      const wrapper = document.createElement('div');
      wrapper.className = 'variant-option__select-wrapper';
      wrapper.appendChild(select);
      wrapper.insertAdjacentHTML(
        'beforeend',
        '<svg aria-hidden="true" class="icon icon-caret" viewBox="0 0 10 6" width="10" height="6"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5"></path></svg>'
      );

      // Hide the original picker UI. Radios stay in the DOM (just hidden) so .click() works.
      const hideSelector =
        'legend, .variant-thumbnail-option__list, .variant-thumbnail-option__selected, .variant-option__button-label';
      for (const node of fieldset.querySelectorAll(hideSelector)) {
        /** @type {HTMLElement} */ (node).style.display = 'none';
      }
      fieldset.classList.add('variant-option--as-dropdown');
      if (labelText) fieldset.appendChild(label);
      fieldset.appendChild(wrapper);
    }
  };

  /** Brand voice: the add-to-cart button reads "Add to bag" in the cart quick-add modal. */
  #relabelAddToBag = () => {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    for (const content of modalContent.querySelectorAll('.add-to-cart-button .add-to-cart-text__content')) {
      const label = content.querySelector('span span') || content;
      if (label.textContent.trim().toLowerCase() !== 'add to bag') label.textContent = 'Add to bag';
    }
  };

  #applyCartQuickAddTweaks = () => {
    this.#convertModalSwatchesToDropdowns();
    this.#relabelAddToBag();
    this.#ensureSubscriptionBlock();
  };

  /**
   * Subscription option in the cart quick-add modal (One Time / Subscribe & Save), mirroring
   * the in-cart cart-line-subscription pattern but applied to the add form: the choice sets the
   * native <input name="selling_plan"> (the Recharge SCI bridge then carries it to the cart).
   * Data — plans, frequencies, discount — comes straight from the product's selling plans.
   */
  async #buildSubscriptionSelector() {
    const url = this.dataset.productUrl;
    if (!url) return;
    const jsUrl = new URL(url, window.location.origin).pathname.replace(/\/+$/, '') + '.js';

    let data = this.#subscriptionDataCache.get(jsUrl);
    if (data === undefined) {
      try {
        const product = await (await fetch(jsUrl)).json();
        data = this.#extractSubscriptionData(product);
      } catch {
        data = null;
      }
      this.#subscriptionDataCache.set(jsUrl, data);
    }
    this.#currentSubscriptionData = data;
    this.#ensureSubscriptionBlock();
  }

  /** @param {any} product */
  #extractSubscriptionData(product) {
    const variant = product?.variants?.[0];
    const allocations = variant?.selling_plan_allocations || [];
    if (allocations.length === 0) return null;

    /** @type {Record<string, string>} */
    const planNames = {};
    for (const group of product.selling_plan_groups || []) {
      for (const plan of group.selling_plans || []) planNames[plan.id] = plan.name;
    }

    const plans = allocations.map((/** @type {any} */ a) => ({
      id: a.selling_plan_id,
      name: planNames[a.selling_plan_id] || 'Subscription',
    }));

    const first = allocations[0];
    const compare = first.compare_at_price || variant.price;
    const percent = compare > first.price ? Math.round(((compare - first.price) / compare) * 100) : 0;
    return { plans, percent };
  }

  #ensureSubscriptionBlock = () => {
    const data = /** @type {any} */ (this.#currentSubscriptionData);
    if (!data) return;
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent || modalContent.querySelector('.quick-add-subscription')) return;
    const sellingPlanInput = /** @type {HTMLInputElement | null} */ (
      modalContent.querySelector('input[name="selling_plan"]')
    );
    if (!sellingPlanInput) return;

    const groupName = 'qa-purchase-type-' + (this.dataset.productUrl || '').replace(/\W+/g, '');
    const showFrequency = data.plans.length > 1;
    const block = document.createElement('div');
    block.className = 'quick-add-subscription';
    block.innerHTML = `
      <div class="quick-add-subscription__options" role="radiogroup">
        <label class="quick-add-subscription__option">
          <input type="radio" name="${groupName}" value="onetime" checked>
          <span>One Time</span>
        </label>
        <label class="quick-add-subscription__option">
          <input type="radio" name="${groupName}" value="subscribe">
          <span>Subscribe &amp; Save ${data.percent}%</span>
        </label>
      </div>
      <div class="quick-add-subscription__frequency" hidden>
        <div class="variant-option__select-wrapper">
          <select class="variant-option__select quick-add-subscription__select" aria-label="Delivery frequency">
            ${data.plans.map((/** @type {any} */ p) => `<option value="${p.id}">${p.name}</option>`).join('')}
          </select>
          <svg aria-hidden="true" class="icon icon-caret" viewBox="0 0 10 6" width="10" height="6"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5"></path></svg>
        </div>
      </div>`;

    const frequency = /** @type {HTMLElement} */ (block.querySelector('.quick-add-subscription__frequency'));
    const freqSelect = /** @type {HTMLSelectElement} */ (block.querySelector('.quick-add-subscription__select'));
    const subscribe = /** @type {HTMLInputElement} */ (block.querySelector('input[value="subscribe"]'));

    const applyState = () => {
      if (subscribe.checked) {
        // Mirror recharge-selling-plan-bridge: a disabled input is excluded from FormData and
        // is ignored by product-form.js (!disabled check), so it MUST be enabled to carry the
        // plan. The Recharge widget normally does this, but it doesn't init in the morphed modal.
        sellingPlanInput.value = freqSelect.value;
        sellingPlanInput.disabled = false;
        frequency.hidden = !showFrequency;
      } else {
        sellingPlanInput.value = '';
        sellingPlanInput.disabled = true;
        frequency.hidden = true;
      }
    };
    for (const input of block.querySelectorAll('input[type="radio"]')) input.addEventListener('change', applyState);
    freqSelect.addEventListener('change', applyState);
    applyState();

    // Tuck the purchase-type toggle into the product header (beside the thumbnail, under the
    // title); fall back to above the variant options if there is no header.
    const header = modalContent.querySelector('.product-header');
    if (header) {
      header.appendChild(block);
    } else {
      const anchor =
        modalContent.querySelector('variant-picker') || modalContent.querySelector('.product-form-buttons');
      anchor?.parentElement?.insertBefore(block, anchor);
    }
  };

  #startSwatchDropdownConversion = () => {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    this.#applyCartQuickAddTweaks();
    // Async: fetch the product's selling plans and insert the subscription option.
    this.#buildSubscriptionSelector();
    this.#swatchDropdownObserver?.disconnect();
    this.#swatchDropdownObserver = new MutationObserver(() => this.#applyCartQuickAddTweaks());
    this.#swatchDropdownObserver.observe(modalContent, { childList: true, subtree: true });
  };

  #stopSwatchDropdownConversion = () => {
    this.#swatchDropdownObserver?.disconnect();
    this.#swatchDropdownObserver = null;
    this.#currentSubscriptionData = null;
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute('stay-visible', false);
        this.#stopSwatchDropdownConversion();
      },
      {
        once: true,
      }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
